import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

export interface Crumb {
    label: ReactNode
    href?: string
}

export const Breadcrumbs: FC<{ items: Array<Crumb>, className?: string }> = ({ items, className }): ReactNode => {
    const { tx, isRtl } = useI18n()
    const Separator = isRtl ? ChevronLeft : ChevronRight

    return (
        <nav aria-label={tx('مسار التصفح', 'Breadcrumb')} className={className}>
            <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                <li>
                    <Link href='/' className='transition-colors hover:text-navy-950'>{tx('الرئيسية', 'Home')}</Link>
                </li>

                {items.map((item, index) => (
                    <li key={index} className='flex items-center gap-1.5'>
                        <Separator className='size-3.5 text-navy-300' aria-hidden='true' />
                        {item.href
                            ? <Link href={item.href} className='transition-colors hover:text-navy-950'>{item.label}</Link>
                            : <span className='font-medium text-navy-900' aria-current='page'>{item.label}</span>}
                    </li>
                ))}
            </ol>
        </nav>
    )
}

interface PageHeaderProps {
    title: ReactNode
    subtitle?: ReactNode
    crumbs?: Array<Crumb>
    children?: ReactNode
    className?: string
}

export const PageHeader: FC<PageHeaderProps> = ({ title, subtitle, crumbs = [], children, className }): ReactNode => {
    return (
        <section className={cn('relative overflow-hidden border-b border-navy-100/70 bg-gradient-to-b from-navy-50/80 to-white', className)}>
            <div className='absolute inset-0 bg-dots opacity-60 [mask-image:linear-gradient(to_bottom,black,transparent)]' aria-hidden='true' />

            <div className='container-page relative flex flex-col gap-4 py-10 sm:py-14'>
                {crumbs.length > 0 && <Breadcrumbs items={crumbs} />}

                <div className='flex flex-col gap-3'>
                    <h1 className='text-balance text-3xl font-bold leading-tight tracking-tight text-navy-950 sm:text-4xl lg:text-5xl'>{title}</h1>
                    {subtitle && <div className='max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg'>{subtitle}</div>}
                </div>

                {children}
            </div>
        </section>
    )
}
